import { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, inr, dt, customerStatus, download, loadRazorpay } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SiteHeader } from "@/components/SiteChrome";
import { StatusBadge } from "@/components/Brand";
import { inputCls } from "@/pages/Auth";

const TABS = ["Bookings", "Invoices", "Profile", "Support"];

const Bookings = () => {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/me/bookings").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  if (!rows) return <p className="text-slate-500">Loading bookings…</p>;
  if (!rows.length) return <p className="rounded-xl border border-dashed p-10 text-center text-slate-500" data-testid="bookings-empty">No bookings yet. <Link to="/" className="font-semibold text-[#0A192F] underline">Book a ride</Link></p>;
  return (
    <div className="space-y-3" data-testid="customer-bookings-list">
      {rows.map((b) => (
        <Link key={b.booking_id} to={`/account/bookings/${b.booking_id}`} data-testid={`booking-row-${b.booking_id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-white p-5 transition-shadow hover:shadow-md">
          <div><p className="font-bold text-[#0A192F]">{b.booking_id} · {b.service_label}</p><p className="text-sm text-slate-500">{dt(b.pickup_at_utc)} · {b.pickup.address}</p></div>
          <div className="flex items-center gap-3"><span className="font-semibold">{b.fare.total_paise != null ? inr(b.fare.total_paise) : "TBC"}</span><StatusBadge value={customerStatus(b.status)} /></div>
        </Link>
      ))}
    </div>
  );
};

export const payInvoice = async (inv, onDone) => {
  try {
    const { data } = await api.post(`/me/invoices/${inv.invoice_id}/pay`);
    if (!(await loadRazorpay())) return toast.error("Could not load payment checkout");
    new window.Razorpay({
      key: data.key_id, order_id: data.order_id, amount: data.amount, currency: "INR", name: "NAMMAAFLEET", description: inv.invoice_number,
      handler: async (resp) => {
        try { await api.post(`/me/invoices/${inv.invoice_id}/verify`, resp); toast.success("Payment verified"); onDone?.(); } catch (e) { toast.error(errMsg(e)); }
      },
    }).open();
  } catch (e) { toast.error(errMsg(e)); }
};

const InvoiceRow = ({ inv, reload }) => (
  <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-white p-5" data-testid={`invoice-row-${inv.invoice_id}`}>
    <div><p className="font-bold text-[#0A192F]">{inv.invoice_number}</p><p className="text-sm text-slate-500">Booking {inv.booking_id} · {dt(inv.issued_at)}</p></div>
    <div className="flex flex-wrap items-center gap-3 text-sm">
      <span>Total <b>{inr(inv.total_paise)}</b></span><span>Outstanding <b>{inr(inv.outstanding_paise)}</b></span><StatusBadge value={inv.payment_status} />
      <button onClick={() => download(`/invoices/${inv.invoice_id}/pdf`, `${inv.invoice_number.replaceAll("/", "-")}.pdf`)} className="rounded-full border px-3 py-1 font-semibold" data-testid={`invoice-download-${inv.invoice_id}`}>PDF</button>
      {inv.outstanding_paise > 0 && <button onClick={() => payInvoice(inv, reload)} className="rounded-full bg-[#FFB703] px-3 py-1 font-semibold text-[#0A192F]" data-testid={`invoice-pay-${inv.invoice_id}`}>Pay now</button>}
    </div>
  </div>
);

const Invoices = () => {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => { api.get("/me/invoices").then((r) => setRows(r.data)); }, []);
  useEffect(() => { load(); }, [load]);
  if (!rows) return <p className="text-slate-500">Loading…</p>;
  if (!rows.length) return <p className="rounded-xl border border-dashed p-10 text-center text-slate-500" data-testid="invoices-empty">No invoices yet. Invoices appear after a trip is completed.</p>;
  return <div className="space-y-3">{rows.map((i) => <InvoiceRow key={i.invoice_id} inv={i} reload={load} />)}</div>;
};

const Profile = () => {
  const { user, refresh } = useAuth();
  const [f, setF] = useState({ name: user.name, phone: user.phone || "", notification_prefs: user.notification_prefs || { email: true, whatsapp: true } });
  const save = async (e) => {
    e.preventDefault();
    try { await api.patch("/me/profile", f); await refresh(); toast.success("Profile saved"); } catch (x) { toast.error(errMsg(x)); }
  };
  const np = (k) => (e) => setF({ ...f, notification_prefs: { ...f.notification_prefs, [k]: e.target.checked } });
  return (
    <form onSubmit={save} className="max-w-md space-y-4" data-testid="profile-form">
      <input className={inputCls} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="profile-name-input" />
      <input className={inputCls} value={f.phone} placeholder="Mobile number" onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="profile-phone-input" />
      <p className="text-sm text-slate-500">{user.email} · Mobile {user.phone_verified ? "verified" : "not verified (OTP not configured)"}</p>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.notification_prefs.email} onChange={np("email")} data-testid="pref-email" /> Email notifications</label>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.notification_prefs.whatsapp} onChange={np("whatsapp")} data-testid="pref-whatsapp" /> WhatsApp notifications</label>
      <button className="rounded-full bg-[#0A192F] px-6 py-2.5 text-sm font-semibold text-white" data-testid="profile-save-btn">Save</button>
    </form>
  );
};

const Support = () => {
  const [f, setF] = useState({ subject: "", message: "", booking_id: "" });
  const send = async (e) => {
    e.preventDefault();
    try { await api.post("/me/support", f); toast.success("Request sent. Our team will get back to you."); setF({ subject: "", message: "", booking_id: "" }); } catch (x) { toast.error(errMsg(x)); }
  };
  return (
    <form onSubmit={send} className="max-w-lg space-y-3" data-testid="support-form">
      <input className={inputCls} placeholder="Subject" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} required data-testid="support-subject-input" />
      <input className={inputCls} placeholder="Booking ID (optional)" value={f.booking_id} onChange={(e) => setF({ ...f, booking_id: e.target.value })} data-testid="support-booking-input" />
      <textarea className={inputCls} rows={4} placeholder="How can we help?" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} required data-testid="support-message-input" />
      <button className="rounded-full bg-[#0A192F] px-6 py-2.5 text-sm font-semibold text-white" data-testid="support-submit-btn">Send</button>
    </form>
  );
};

export default function Account() {
  const { user } = useAuth();
  const [tab, setTab] = useState("Bookings");
  const C = { Bookings, Invoices, Profile, Support }[tab];
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <SiteHeader />
      <main className="mx-auto max-w-5xl px-5 py-12">
        <h1 className="text-3xl font-bold text-[#0A192F]" data-testid="account-title">Hello, {user.name}</h1>
        <div className="mt-6 flex gap-1 overflow-x-auto border-b">
          {TABS.map((t) => <button key={t} onClick={() => setTab(t)} data-testid={`account-tab-${t.toLowerCase()}`} className={`px-4 py-2.5 text-sm font-semibold ${tab === t ? "border-b-2 border-[#FFB703] text-[#0A192F]" : "text-slate-500"}`}>{t}</button>)}
        </div>
        <div className="mt-8"><C /></div>
      </main>
    </div>
  );
}

export function AccountBooking() {
  const { id } = useParams();
  const [b, setB] = useState(null);
  const [cp, setCp] = useState(null);
  const load = useCallback(() => { api.get(`/me/bookings/${id}`).then((r) => setB(r.data)).catch(() => setB(false)); }, [id]);
  useEffect(() => { load(); }, [load]);
  const cancel = async () => {
    try { await api.post(`/me/bookings/${id}/cancel`, { reason: "Cancelled by customer" }); toast.success("Booking cancelled"); setCp(null); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const preview = async () => { const { data } = await api.get(`/me/bookings/${id}/cancel-preview`); setCp(data); };
  if (b === null) return <div className="p-16">Loading…</div>;
  if (!b) return <div className="p-16" data-testid="booking-not-found">Booking not found.</div>;
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <SiteHeader />
      <main className="mx-auto max-w-4xl space-y-6 px-5 py-12" data-testid="customer-booking-detail">
        <Link to="/account" className="text-sm text-slate-500">← My bookings</Link>
        <div className="flex flex-wrap items-center justify-between gap-3"><h1 className="text-2xl font-bold text-[#0A192F]">{b.booking_id}</h1><StatusBadge value={customerStatus(b.status)} testid="customer-booking-status" /></div>
        <div className="grid gap-6 md:grid-cols-2">
          <div className="rounded-2xl border bg-white p-6 text-sm">
            <p className="font-semibold text-[#0A192F]">{b.service_label}</p>
            <p className="mt-2">From: {b.pickup.address}</p><p>To: {b.drop?.address || "As per package"}</p><p>When: {dt(b.pickup_at_utc)}</p>
            <p>Category: {b.category} · {b.rate_class}</p><p>Payment: <StatusBadge value={b.payment_status} /></p>
          </div>
          <div className="rounded-2xl border bg-white p-6 text-sm" data-testid="assignment-panel">
            <p className="font-semibold text-[#0A192F]">Driver & vehicle</p>
            {b.assignment ? (
              <div className="mt-2 space-y-1"><p>Driver: <b>{b.assignment.driver_name}</b> · +91 {b.assignment.driver_phone}</p><p>Vehicle: {b.assignment.vehicle_label} · {b.assignment.registration}</p></div>
            ) : <p className="mt-2 text-slate-500">Details will appear once a driver and vehicle are assigned.</p>}
          </div>
        </div>
        <div className="rounded-2xl border bg-white p-6 text-sm">
          <p className="mb-3 font-semibold text-[#0A192F]">Fare breakdown · Rate card {b.rate_card_version}</p>
          {b.fare.lines?.map((l, i) => <div key={i} className="flex justify-between py-1"><span>{l.label}</span><span>{inr(l.amount_paise)}</span></div>)}
          <div className="mt-2 flex justify-between border-t pt-2 font-bold"><span>Total {b.fare.fare_status !== "fixed" && "(estimated)"}</span><span>{b.fare.total_paise != null ? inr(b.fare.total_paise) : "To be confirmed"}</span></div>
          {b.fare_adjustments?.map((a) => <p key={a.adj_id} className="mt-1 text-amber-800">Fare updated to {inr(a.new_total_paise)} — {a.reason}</p>)}
          <p className="mt-2 text-xs text-slate-500">Excludes: {b.fare.exclusions?.join(", ")}</p>
        </div>
        {b.invoice && <InvoiceRow inv={b.invoice} reload={load} />}
        {["Pending Admin Assignment", "Assigned", "Confirmed"].includes(b.status) && (
          <div className="rounded-2xl border bg-white p-6">
            {!cp ? <button onClick={preview} className="text-sm font-semibold text-red-700" data-testid="cancel-booking-btn">Cancel booking</button> : (
              <div className="flex flex-wrap items-center gap-4 text-sm" data-testid="cancel-confirm-panel">
                <span>Cancellation charge: <b>{inr(cp.charge_paise)}</b> (free up to {cp.free_cancel_hours}h before pickup)</span>
                <button onClick={cancel} className="rounded-full bg-red-600 px-4 py-2 font-semibold text-white" data-testid="cancel-confirm-btn">Confirm cancellation</button>
                <button onClick={() => setCp(null)} className="text-slate-500" data-testid="cancel-dismiss-btn">Keep booking</button>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
